import { useEffect, useMemo, useState } from "react";
import {
  mergeWorkspacesSources,
  normalizeWorkspacesForUi,
  workspaceEntriesToSelectOptions,
} from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import { getPosWorkspacesDocument } from "app/services/api/accessSystemPosWorkspaces";
import { generateToken } from "app/services/api/management";

/**
 * Options for the device workspace `Select` on {@link GeneralSection}.
 *
 * - **Customer-detail Devices** (`posWorkspacesCustomerId` is `null` | `string`): list is loaded only from
 *   `GET /api/v1/access-system/posWorkspace` with `Authorization: Bearer` set to the edited customer’s JWT
 *   from {@link generateToken} (not the Admin UI session token).
 * - **Other screens** (`posWorkspacesCustomerId === undefined`): legacy merged sources (dsokSettings / form).
 *
 * @param {{
 *   posWorkspacesCustomerId: string | null | undefined,
 *   posWorkspacesBeDomain: string | null | undefined,
 *   customerDetailLoading: boolean,
 *   dsokSettings: object | null | undefined,
 *   detailCustomer: object | null | undefined,
 *   formCustomerWorkspaces: object | null | undefined,
 * }} params
 * @returns {{ workspacesData: { uuid: string, name: string }[], showWorkspaceRowLoading: boolean }}
 */
export function useDeviceWorkspaceSelectOptions({
  posWorkspacesCustomerId,
  posWorkspacesBeDomain,
  customerDetailLoading,
  dsokSettings,
  detailCustomer,
  formCustomerWorkspaces,
}) {
  const [posApiWorkspaces, setPosApiWorkspaces] = useState(null);

  const posWorkspacesApiMode = posWorkspacesCustomerId !== undefined;

  useEffect(() => {
    if (!posWorkspacesApiMode) {
      setPosApiWorkspaces(null);
      return undefined;
    }
    const id =
      typeof posWorkspacesCustomerId === "string"
        ? posWorkspacesCustomerId.trim()
        : "";
    if (!id) {
      setPosApiWorkspaces(null);
      return undefined;
    }
    let cancelled = false;
    setPosApiWorkspaces(null);
    (async () => {
      try {
        const accessToken = await generateToken(id);
        if (cancelled) return;
        if (!accessToken) {
          setPosApiWorkspaces([]);
          return;
        }
        const doc = await getPosWorkspacesDocument({
          accessToken,
          beDomain: posWorkspacesBeDomain,
        });
        if (cancelled) return;
        const { data } = normalizeWorkspacesForUi(doc || {});
        setPosApiWorkspaces(workspaceEntriesToSelectOptions(data));
      } catch {
        if (cancelled) return;
        setPosApiWorkspaces([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [posWorkspacesApiMode, posWorkspacesCustomerId, posWorkspacesBeDomain]);

  const legacyWorkspacesData = useMemo(() => {
    if (posWorkspacesCustomerId !== undefined) return [];
    const buckets = [
      mergeWorkspacesSources(
        dsokSettings?.customer?.workspaces,
        dsokSettings?.workspaces,
      ),
      mergeWorkspacesSources(
        detailCustomer?.dsokSettings?.customer?.workspaces,
        detailCustomer?.dsokSettings?.workspaces,
      ),
      formCustomerWorkspaces,
    ].filter(Boolean);

    const mergedEntries = [];
    for (const raw of buckets) {
      if (!raw || typeof raw !== "object") continue;
      mergedEntries.push(...normalizeWorkspacesForUi(raw).data);
    }
    return workspaceEntriesToSelectOptions(mergedEntries);
  }, [
    posWorkspacesCustomerId,
    dsokSettings?.customer?.workspaces,
    dsokSettings?.workspaces,
    detailCustomer?.dsokSettings?.customer?.workspaces,
    detailCustomer?.dsokSettings?.workspaces,
    formCustomerWorkspaces,
  ]);

  const hasEditedCustomerId =
    typeof posWorkspacesCustomerId === "string" &&
    posWorkspacesCustomerId.trim() !== "";
  const showWorkspaceRowLoading =
    posWorkspacesApiMode &&
    (customerDetailLoading ||
      (hasEditedCustomerId && posApiWorkspaces === null));
  const workspacesData = posWorkspacesApiMode
    ? posApiWorkspaces ?? []
    : legacyWorkspacesData;

  return { workspacesData, showWorkspaceRowLoading };
}
